import { useState, type ReactNode } from 'react'
import { cn } from '#app/utils/misc.tsx'

export interface ProductWalkthroughStep {
	id: string
	step: string
	title: string
	body: string
	screenshotLabel: string
	screenshotTone?: 'light' | 'dark'
	screenshotAccent?: boolean
	screenshotSrc?: string
}

export interface ProductWalkthroughAccordionProps {
	eyebrow?: string
	title?: string
	subtitle?: string
	background?: 'default' | 'muted' | 'primary-light'
	/**
	 * Brand label shown in the placeholder screenshot chrome (e.g. "semyla").
	 * Defaults to "arkeias" to preserve existing behaviour.
	 */
	productLabel?: string
	steps: ProductWalkthroughStep[]
}

const backgroundStyles: Record<
	NonNullable<ProductWalkthroughAccordionProps['background']>,
	string
> = {
	default: 'bg-background',
	muted: 'bg-muted/30',
	'primary-light': 'bg-primary/5',
}

export function ProductWalkthroughAccordion({
	eyebrow,
	title,
	subtitle,
	background = 'default',
	productLabel = 'arkeias',
	steps,
}: ProductWalkthroughAccordionProps) {
	const [activeIndex, setActiveIndex] = useState(0)
	const current = steps[activeIndex] ?? steps[0]

	if (steps.length === 0) return null

	return (
		<section
			className={cn(
				'px-6 py-20 sm:px-8 lg:px-12',
				backgroundStyles[background],
			)}
		>
			<div className="mx-auto flex max-w-7xl flex-col gap-12">
				{(eyebrow || title || subtitle) && (
					<header className="mx-auto flex max-w-3xl flex-col items-center gap-4 text-center">
						{eyebrow && (
							<span className="text-label text-muted-foreground">
								{eyebrow}
							</span>
						)}
						{title && <h2 className="text-title-2">{title}</h2>}
						{subtitle && (
							<p className="text-muted-foreground max-w-2xl text-[15px] leading-relaxed md:text-base">
								{subtitle}
							</p>
						)}
					</header>
				)}

				<div className="grid gap-8 lg:grid-cols-[340px_minmax(0,1fr)] lg:items-start xl:grid-cols-[360px_minmax(0,1fr)]">
					<ol className="border-primary/10 divide-primary/10 m-0 flex list-none flex-col divide-y border-y p-0">
						{steps.map((step, idx) => {
							const isActive = idx === activeIndex
							return (
								<li key={step.id}>
									<button
										type="button"
										aria-expanded={isActive}
										aria-controls={`product-walkthrough-panel-${step.id}`}
										onClick={() => setActiveIndex(idx)}
										className="group flex w-full flex-col text-left"
									>
										<span className="grid grid-cols-[8px_24px_minmax(0,1fr)_20px] items-center gap-3 py-4">
											<span
												className={cn(
													'size-2 shrink-0 rounded-full transition-all duration-300',
													isActive ? 'bg-gold' : 'bg-border',
												)}
											/>
											<span className="text-muted-foreground text-right font-mono text-[11px]">
												{step.step}
											</span>
											<span
												className={cn(
													'flex-1 text-[15px] leading-tight font-semibold transition-colors',
													isActive
														? 'text-primary'
														: 'text-muted-foreground group-hover:text-muted-foreground',
												)}
											>
												{step.title}
											</span>
											<span
												className={cn(
													'text-muted-foreground shrink-0 text-lg leading-none transition-transform duration-300',
													isActive && 'text-foreground rotate-45',
												)}
												aria-hidden
											>
												+
											</span>
										</span>
									</button>
									<div
										id={`product-walkthrough-panel-${step.id}`}
										className={cn(
											'grid transition-[grid-template-rows,opacity] duration-300 ease-out',
											isActive
												? 'grid-rows-[1fr] opacity-100'
												: 'grid-rows-[0fr] opacity-0',
										)}
									>
										<div className="min-h-0 overflow-hidden">
											<p className="text-muted-foreground pb-5 pl-[56px] text-[13px] leading-relaxed">
												{step.body}
											</p>
											<div className="pb-6 lg:hidden">
												<JourneyScreenshot
													label={step.screenshotLabel}
													tone={step.screenshotTone}
													accent={step.screenshotAccent}
													src={step.screenshotSrc}
													productLabel={productLabel}
												/>
											</div>
										</div>
									</div>
								</li>
							)
						})}
					</ol>

					<div className="hidden lg:sticky lg:top-20 lg:block lg:self-start">
						{current && (
							<JourneyScreenshot
								key={current.id}
								label={current.screenshotLabel}
								tone={current.screenshotTone}
								accent={current.screenshotAccent}
								src={current.screenshotSrc}
								productLabel={productLabel}
							/>
						)}
						<div className="mt-5 flex items-center justify-center gap-2">
							{steps.map((step, i) => (
								<span
									key={step.id}
									aria-hidden
									className={cn(
										'h-1 rounded-full transition-all',
										activeIndex === i ? 'bg-foreground w-8' : 'bg-border w-3',
									)}
								/>
							))}
						</div>
					</div>
				</div>
			</div>
		</section>
	)
}

interface JourneyScreenshotProps {
	label: string
	tone?: 'light' | 'dark'
	accent?: boolean
	src?: string
	productLabel?: string
}

function JourneyScreenshot({
	label,
	tone = 'light',
	accent,
	src,
	productLabel = 'arkeias',
}: JourneyScreenshotProps) {
	if (src) {
		return (
			<figure className="aspect-[1.92] overflow-hidden rounded-[18px] border border-black/10 shadow-[0_32px_80px_-24px_rgb(0_0_0_/_0.35)]">
				<img
					src={src}
					alt={label}
					className="h-full w-full object-cover object-top"
				/>
			</figure>
		)
	}
	return (
		<JourneyScreenshotPlaceholder
			label={label}
			tone={tone}
			accent={accent}
			productLabel={productLabel}
		/>
	)
}

function JourneyScreenshotPlaceholder({
	label,
	tone,
	accent,
	productLabel,
}: Required<Pick<JourneyScreenshotProps, 'label' | 'tone' | 'productLabel'>> &
	Pick<JourneyScreenshotProps, 'accent'>) {
	const toneClass =
		tone === 'dark'
			? 'bg-[oklch(0.16_0_0)] text-white'
			: 'bg-card text-foreground'
	return (
		<div
			aria-label={label}
			role="img"
			className={cn(
				'relative aspect-[1.74] overflow-hidden rounded-[18px] border border-black/10 shadow-[0_32px_80px_-24px_rgb(0_0_0_/_0.35)]',
				toneClass,
			)}
		>
			<div className="absolute inset-x-0 top-0 z-10 flex items-center gap-1.5 border-b border-current/10 bg-current/5 px-3 py-2 backdrop-blur">
				<span className="size-2 rounded-full bg-current/30" aria-hidden />
				<span className="size-2 rounded-full bg-current/20" aria-hidden />
				<span className="size-2 rounded-full bg-current/15" aria-hidden />
				<span className="text-label ml-3 opacity-50">
					{productLabel} / {label.toLowerCase()}
				</span>
			</div>
			<div className="relative flex h-full w-full flex-col gap-3 px-5 pt-12 pb-5">
				<JourneyFakeProductLayout label={label} accent={accent} />
				<div className="mt-auto flex items-end justify-between gap-3">
					<span className="text-label opacity-50">placeholder</span>
				</div>
			</div>
		</div>
	)
}

function JourneyFakeProductLayout({
	label,
	accent,
}: {
	label: string
	accent?: boolean
}): ReactNode {
	return (
		<div className="flex h-full min-h-0 flex-1 flex-col gap-3">
			<div className="flex items-center justify-between">
				<div className="flex flex-col gap-1.5">
					<div className="h-2 w-24 rounded-sm bg-current/25" />
					<div className="h-3.5 w-48 rounded-sm bg-current/55" />
				</div>
				{accent && (
					<div className="text-label text-muted-foreground border-border rounded-full border px-2.5 py-1">
						criterio
					</div>
				)}
			</div>
			<div className="grid flex-1 grid-cols-5 gap-3">
				<div className="col-span-1 flex flex-col gap-1.5">
					{Array.from({ length: 6 }).map((_, i) => (
						<div
							key={`row-${label}-${i}`}
							className={cn(
								'h-1.5 rounded-sm bg-current/20',
								i === 1 && 'bg-current/55',
							)}
						/>
					))}
				</div>
				<div className="col-span-4 flex flex-col gap-2">
					<div className="grid grid-cols-3 gap-2">
						{Array.from({ length: 3 }).map((_, i) => (
							<div
								key={`tile-${label}-${i}`}
								className="aspect-[4/3] rounded-[6px] border border-current/15 bg-current/[0.06]"
							/>
						))}
					</div>
					<div className="grid flex-1 grid-cols-2 gap-2">
						<div className="rounded-[6px] border border-current/10 bg-current/[0.04] p-2">
							<div className="mb-1.5 h-2 w-16 rounded-sm bg-current/35" />
							<div className="flex flex-col gap-1">
								<div className="h-1.5 w-full rounded-sm bg-current/20" />
								<div className="h-1.5 w-11/12 rounded-sm bg-current/20" />
								<div className="h-1.5 w-9/12 rounded-sm bg-current/20" />
								<div className="h-1.5 w-10/12 rounded-sm bg-current/20" />
							</div>
						</div>
						<div className="rounded-[6px] border border-current/10 bg-current/[0.04] p-2">
							<div className="mb-1.5 h-2 w-14 rounded-sm bg-current/35" />
							<div className="space-y-1">
								<div className="flex items-center gap-1.5">
									<div className="bg-foreground size-2 rounded-full" />
									<div className="h-1.5 flex-1 rounded-sm bg-current/25" />
								</div>
								<div className="flex items-center gap-1.5">
									<div className="size-2 rounded-full bg-current/30" />
									<div className="h-1.5 flex-1 rounded-sm bg-current/20" />
								</div>
								<div className="flex items-center gap-1.5">
									<div className="size-2 rounded-full bg-current/30" />
									<div className="h-1.5 flex-1 rounded-sm bg-current/20" />
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	)
}
